import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, FileSearch, Mail } from 'lucide-react';
import { useNotificationSound } from '../../hooks/useNotificationSound.js';
import { useAuth } from '@/components/Auth/AuthContext';
import PageHeader from '@/components/Layout/PageHeader';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getCompanyAiFocusDomId, matchesCompanyAiFocusRecord, readCompanyAiFocusTarget, readCompanyAiFollowUpSeed, readCompanyAiPeriodFilter } from '@/utils/companyAiNavigation';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { replaceInternalOwnerEmails } from '@/utils/internalOwnerLabel';
import { formatSuburbLabel } from '@/utils/suburbText';
import { hasDateRange, normalizeDateOnlyInput } from '@/utils/listDateFilters';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { Button, Card, DataRow, EmptyState, Input, Modal, PageSection, Select, Textarea } from '@/ui/components';
import ListPaginationControls from '@/components/List/ListPaginationControls';
import { ProjectDocumentStatusPanel } from '@/components/projects/ProjectDocumentStatusPanel';
import '@/pages/dataGridFocus.css';

// Azure SQL ProjectList schema from api/projects/index.ts
interface Project {
  ProjectId: number;
  ProjectCode: string;
  ProjectName: string;
  Status: string;
  SiteAddress?: string;
  Suburb?: string;
  PostCode?: number | string | null;
  Council?: string;
  ClientEmail?: string | null;
  ClientPhone?: string | null;
  Description?: string;
  SiteId?: string;
  DriveId?: string;
  FolderItemId?: string;
  FolderPath?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  QuotationId?: number | string | null;
  LinkedQuotationNumber?: string | null;
  ProjectScope?: string | null;
  ProjectType?: string | null;
  StructuralFee?: number | string | null;
  CivilFee?: number | string | null;
  SoilReportFee?: number | string | null;
  QuotationAmount?: number | string | null;
  LinkedTaskCount?: number | string | null;
  OpenTaskCount?: number | string | null;
  SuggestedScore?: number | string | null;
  LatestTaskId?: number | string | null;
  ProjectLocation?: string | null;
  InvoiceCount?: number | string | null;
  UnpaidInvoiceCount?: number | string | null;
  CurrentInvoiceCount?: number | string | null;
  CivilInvoiceCount?: number | string | null;
  CurrentUnpaidInvoiceAmount?: number | string | null;
  CivilUnpaidInvoiceAmount?: number | string | null;
  TaskTimeline?: unknown[] | string | null;
  CouncilEngineerEmail?: string | null;
  CouncilDrainageEmailLastSentAt?: string | null;
  CouncilDrainageEmailLastSentBy?: string | null;
}

type ProjectTaskCategory = 'Civil' | 'Structural' | 'To Do';
type ProjectTaskType = 'Standard' | 'Inspection' | 'RFI';
type ProjectTaskStatus = 'To Do' | 'In Progress' | 'Completed' | 'Overdue';
type ProjectTaskTimelineItem = {
  TaskId?: number | string | null;
  TaskNumber?: string | null;
  Title?: string | null;
  Category?: string | null;
  TaskType?: string | null;
  Priority?: string | null;
  Status?: string | null;
  StartDate?: string | null;
  DueDate?: string | null;
  CompletedDate?: string | null;
};
type ProjectTaskTimelineMarker = {
  key: string;
  taskId: number | string | null;
  taskNumber: string;
  title: string;
  category: ProjectTaskCategory;
  taskType: ProjectTaskType;
  status: ProjectTaskStatus;
  detailLabel: string;
  lane: 'top' | 'bottom';
  bubbleOffset: number;
  tooltipSide: 'left' | 'right';
};
type ProjectTaskTimelineGroup = {
  key: string;
  dateLabel: string;
  leftPercent: number;
  dotCategory: ProjectTaskCategory;
  dotStatus: ProjectTaskStatus;
  markers: ProjectTaskTimelineMarker[];
};

type ProjectSortBy = 'suggested' | 'address' | 'code' | 'status' | 'updated';
type SortDirection = 'asc' | 'desc';
type ProjectDisciplineFilter = 'all' | 'civil' | 'structural';
type ProjectLifecycleFilter = 'active' | 'on-hold' | 'inactive' | 'completed' | 'cancelled' | 'archived' | 'all';
const LIST_PAGE_SIZE = 20;
const LIST_PAGE_SIZE_OPTIONS = [20, 50, 100];
const PROJECT_STATUS_OPTIONS = ['Active', 'Inactive', 'On Hold', 'Completed', 'Cancelled'] as const;
const PROJECT_STATUS_FILTER_OPTIONS: Array<{ value: ProjectLifecycleFilter; label: string }> = [
  { value: 'active', label: 'Active only' },
  { value: 'on-hold', label: 'On Hold only' },
  { value: 'inactive', label: 'Inactive only' },
  { value: 'completed', label: 'Completed only' },
  { value: 'cancelled', label: 'Cancelled only' },
  { value: 'archived', label: 'Archived/deleted only' },
  { value: 'all', label: 'All statuses' },
];
const TIMELINE_LEFT_BOUND_PERCENT = 8;
const TIMELINE_LEFT_CENTER_GAP_PERCENT = 44;
const TIMELINE_CENTER_PERCENT = 50;
const TIMELINE_RIGHT_CENTER_GAP_PERCENT = 56;
const TIMELINE_RIGHT_BOUND_PERCENT = 92;
const WILSON_EMAIL = 'wd@linfinityeng.com';
const YUAN_EMAIL = 'yl@linfinityeng.com';
const COUNCIL_DRAINAGE_EMAIL_SENDER = 'wdu@eqeng.com.au';
const COUNCIL_DRAINAGE_EMAIL_BCC = 'yl@linfinityeng.com';

type CouncilEmailDraft = {
  councilEngineerEmail: string;
  subject: string;
  message: string;
};

function getDefaultSortDirection(sortBy: ProjectSortBy): SortDirection {
  return 'desc';
}

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCurrency(value: unknown): string {
  return formatCurrencyValue(value, 0);
}

function formatCurrencyValue(value: unknown, maximumFractionDigits: number): string {
  const parsed = asNumber(value);
  if (parsed === null) {
    return maximumFractionDigits > 0 ? '$0.00' : 'Price pending';
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: maximumFractionDigits > 0 ? maximumFractionDigits : 0,
    maximumFractionDigits,
  }).format(parsed);
}

function normalizeEmailMessageText(value: unknown): string {
  return String(value ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

function buildCouncilDrainageEmailMessage(): string {
  return [
    'To Whom It May Concern,',
    '',
    "I hope you're well. I'm writing to follow up on the drainage plans we submitted for approval and to check on the current status of the assessment.",
    'We are keen to progress the project and would appreciate an update on the current status of the review, as well as whether any further information or revisions are required from our end.',
    'Thank you in advance, and please let me know if you need anything further.',
  ].join('\n');
}

function stripStateAndPostcode(value: string): string {
  return value
    .replace(/\b(?:VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b(?:\s+\d{4})?$/i, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/,\s*$/g, '')
    .trim();
}

function resolveProjectStreet(value?: string | null): string | null {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  const firstSegment = normalized.split(',').map((segment) => segment.trim()).filter(Boolean)[0] || normalized;
  return stripStateAndPostcode(firstSegment) || firstSegment;
}

function buildCouncilDrainageEmailSubject(project: Project): string {
  const projectCode = String(project.ProjectCode || project.ProjectId || '').trim();
  const street = resolveProjectStreet(project.SiteAddress || project.ProjectLocation);
  const suburb = formatSuburbLabel(project.Suburb) || String(project.Suburb || '').trim();
  const location = [street, suburb].filter(Boolean).join(', ');
  return [projectCode, location].filter(Boolean).join(' - ') + ' - Drainage Approval Update';
}

function createCouncilEmailDraft(project: Project): CouncilEmailDraft {
  return {
    councilEngineerEmail: String(project.CouncilEngineerEmail || '').trim(),
    subject: buildCouncilDrainageEmailSubject(project),
    message: buildCouncilDrainageEmailMessage(),
  };
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value.trim());
}

function hasPositiveCivilFee(project: Project): boolean {
  const civilFee = asNumber(project.CivilFee);
  return civilFee !== null && civilFee > 0;
}

function formatLastTraceAge(value?: string | null): string {
  if (!value) return 'No trace yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Trace date unknown';
  const diffMs = Math.max(0, Date.now() - parsed.getTime());
  const daysAgo = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return `Last trace: ${daysAgo}d ago`;
}

function normalizeComparableText(value?: string | null): string {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeTaskCategory(value?: string | null): ProjectTaskCategory {
  const normalized = normalizeComparableText(value);
  if (normalized === 'civil') return 'Civil';
  if (normalized === 'structural' || normalized === 'structure') return 'Structural';
  return 'To Do';
}

function normalizeTaskType(value?: string | null): ProjectTaskType {
  const normalized = normalizeComparableText(value);
  if (normalized === 'rfi') return 'RFI';
  if (normalized === 'inspection' || normalized === 'inspect') return 'Inspection';
  return 'Standard';
}

function normalizeTaskStatus(value?: string | null): ProjectTaskStatus {
  const normalized = normalizeComparableText(value);
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') return 'Completed';
  if (normalized === 'in progress' || normalized === 'progress' || normalized === 'ongoing') return 'In Progress';
  if (normalized === 'overdue') return 'Overdue';
  return 'To Do';
}

function normalizeTimelineDate(value?: string | null): string | null {
  const normalized = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }
  const parsed = new Date(`${normalized}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : normalized;
}

function parseProjectTaskTimeline(value: unknown): ProjectTaskTimelineItem[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is ProjectTaskTimelineItem => Boolean(item) && typeof item === 'object');
  }

  const raw = String(value || '').trim();
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is ProjectTaskTimelineItem => Boolean(item) && typeof item === 'object')
      : [];
  } catch {
    return [];
  }
}

function getSuggestedProjectScore(project: Project): number {
  const suppliedScore = asNumber(project.SuggestedScore);
  if (suppliedScore !== null) return suppliedScore;

  const tasks = parseProjectTaskTimeline(project.TaskTimeline);
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  let score = (asNumber(project.OpenTaskCount) ?? 0) * 10;

  for (const task of tasks) {
    const status = normalizeComparableText(task.Status);
    if (['completed', 'complete', 'done'].includes(status)) continue;
    const priority = normalizeComparableText(task.Priority);
    if (priority === 'critical') score += 140;
    else if (priority === 'high') score += 90;
    else if (priority === 'medium') score += 30;
    if (status === 'overdue') score += 220;
    else if (['in progress', 'progress', 'ongoing'].includes(status)) score += 30;

    const dueDate = normalizeTimelineDate(task.DueDate);
    if (!dueDate) continue;
    const dueTime = new Date(`${dueDate}T00:00:00`).getTime();
    const days = Math.round((dueTime - todayStart) / 86_400_000);
    if (days < 0) score += 300 + Math.min(-days, 30) * 10;
    else if (days === 0) score += 250;
    else if (days <= 3) score += 180;
    else if (days <= 7) score += 100;
  }

  return score;
}

function formatTaskTimelineDate(value?: string | null): string {
  const normalized = normalizeTimelineDate(value);
  if (!normalized) return 'Date pending';
  const parsed = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Date pending';
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function getTaskTimelineAnchorDate(task: ProjectTaskTimelineItem): string | null {
  return normalizeTimelineDate(task.DueDate) || normalizeTimelineDate(task.StartDate) || normalizeTimelineDate(task.CompletedDate);
}

function getTaskTimelineDetailLabel(task: ProjectTaskTimelineItem): string {
  const dueDate = normalizeTimelineDate(task.DueDate);
  if (dueDate) {
    return `Due ${formatTaskTimelineDate(dueDate)}`;
  }
  const startDate = normalizeTimelineDate(task.StartDate);
  if (startDate) {
    return `Start ${formatTaskTimelineDate(startDate)}`;
  }
  const completedDate = normalizeTimelineDate(task.CompletedDate);
  if (completedDate) {
    return `Completed ${formatTaskTimelineDate(completedDate)}`;
  }
  return 'Date pending';
}

function getTaskTimelineStatus(task: ProjectTaskTimelineItem): ProjectTaskStatus {
  const status = normalizeTaskStatus(task.Status);
  if (status === 'Completed' || status === 'Overdue') {
    return status;
  }

  const dueDay = toUtcDayNumber(normalizeTimelineDate(task.DueDate));
  const todayDay = toUtcDayNumber(getTodayTimelineDate());
  return dueDay !== null && todayDay !== null && dueDay < todayDay ? 'Overdue' : status;
}

function getTodayTimelineDate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toUtcDayNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(parsed) ? null : Math.round(parsed / 86400000);
}

function clampTimelinePosition(value: number): number {
  return Math.min(Math.max(value, 6), 94);
}

function distributeTimelinePositions(count: number, start: number, end: number): number[] {
  if (count <= 0) {
    return [];
  }
  if (count === 1) {
    return [start + ((end - start) / 2)];
  }
  return Array.from({ length: count }, (_, index) => start + ((end - start) * index) / (count - 1));
}

function resolveTimelineGroupDotCategory(markers: ProjectTaskTimelineMarker[]): ProjectTaskCategory {
  if (markers.length === 0) return 'To Do';
  const firstCategory = markers[0].category;
  return markers.every((marker) => marker.category === firstCategory) ? firstCategory : 'To Do';
}

function resolveTimelineGroupDotStatus(markers: ProjectTaskTimelineMarker[]): ProjectTaskStatus {
  if (markers.some((marker) => marker.status === 'Overdue')) return 'Overdue';
  if (markers.every((marker) => marker.status === 'Completed')) return 'Completed';
  if (markers.some((marker) => marker.status === 'In Progress')) return 'In Progress';
  return 'To Do';
}

function assignLaneBubbleOffsets(markers: ProjectTaskTimelineMarker[]): ProjectTaskTimelineMarker[] {
  if (markers.length <= 1) {
    return markers.map((marker) => ({ ...marker, bubbleOffset: 0 }));
  }
  const span = 68;
  const startOffset = span / 2;
  const step = span / (markers.length - 1);
  return markers.map((marker, index) => ({
    ...marker,
    bubbleOffset: -startOffset + index * step,
  }));
}

function buildProjectTaskTimeline(project: Project): { groups: ProjectTaskTimelineGroup[]; overflowCount: number; todayLeftPercent: number } {
  const allTasks = parseProjectTaskTimeline(project.TaskTimeline);
  const datedTasks = allTasks.filter((task) => getTaskTimelineAnchorDate(task));
  const parsedTasks = datedTasks;
  if (parsedTasks.length === 0) {
    return { groups: [], overflowCount: 0, todayLeftPercent: TIMELINE_CENTER_PERCENT };
  }

  const normalizedTasks = parsedTasks.map((task, index) => {
    const anchorDate = getTaskTimelineAnchorDate(task);
    return {
      task,
      index,
      anchorDate,
      anchorDay: toUtcDayNumber(anchorDate),
      lane: 'top' as const,
    };
  }).filter((entry): entry is typeof entry & { anchorDay: number; anchorDate: string } => entry.anchorDay !== null && entry.anchorDate !== null);

  if (normalizedTasks.length === 0) {
    return { groups: [], overflowCount: 0, todayLeftPercent: TIMELINE_CENTER_PERCENT };
  }

  const groupedTasks = new Map<string, typeof normalizedTasks>();
  normalizedTasks.forEach((entry) => {
    const existing = groupedTasks.get(entry.anchorDate) || [];
    existing.push(entry);
    groupedTasks.set(entry.anchorDate, existing);
  });

  const sortedGroups = Array.from(groupedTasks.entries())
    .map(([anchorDate, entries]) => ({
      anchorDate,
      anchorDay: entries[0].anchorDay,
      entries,
    }))
    .sort((a, b) => a.anchorDay - b.anchorDay);

  const todayDay = toUtcDayNumber(getTodayTimelineDate());
  const pastGroups = todayDay === null ? [] : sortedGroups.filter((group) => group.anchorDay < todayDay);
  const todayGroups = todayDay === null ? [] : sortedGroups.filter((group) => group.anchorDay === todayDay);
  const futureGroups = todayDay === null ? sortedGroups : sortedGroups.filter((group) => group.anchorDay > todayDay);

  const groupPositions = new Map<string, number>();
  distributeTimelinePositions(
    pastGroups.length,
    TIMELINE_LEFT_BOUND_PERCENT,
    TIMELINE_LEFT_CENTER_GAP_PERCENT
  ).forEach((position, index) => {
    groupPositions.set(pastGroups[index].anchorDate, position);
  });
  todayGroups.forEach((group) => {
    groupPositions.set(group.anchorDate, TIMELINE_CENTER_PERCENT);
  });
  distributeTimelinePositions(
    futureGroups.length,
    TIMELINE_RIGHT_CENTER_GAP_PERCENT,
    TIMELINE_RIGHT_BOUND_PERCENT
  ).forEach((position, index) => {
    groupPositions.set(futureGroups[index].anchorDate, position);
  });

  const groups = sortedGroups.map((group) => {
    const groupLeftPercent = groupPositions.get(group.anchorDate)
      ?? (todayDay !== null && group.anchorDay === todayDay ? TIMELINE_CENTER_PERCENT : clampTimelinePosition(TIMELINE_CENTER_PERCENT));

    const baseMarkers = group.entries.map((entry) => {
      const taskId = asNumber(entry.task.TaskId) ?? entry.task.TaskId ?? null;
      const taskNumber = String(entry.task.TaskNumber || (taskId ? `T${taskId}` : `T${entry.index + 1}`)).trim();
      const title = String(entry.task.Title || taskNumber).trim() || taskNumber;
      return {
        key: `${taskId || taskNumber}-${entry.index}`,
        taskId,
        taskNumber,
        title,
        category: normalizeTaskCategory(entry.task.Category),
        taskType: normalizeTaskType(entry.task.TaskType),
        status: getTaskTimelineStatus(entry.task),
        detailLabel: getTaskTimelineDetailLabel(entry.task),
        lane: 'top' as const,
        bubbleOffset: 0,
        tooltipSide: groupLeftPercent > 72 ? 'left' : 'right',
      } satisfies ProjectTaskTimelineMarker;
    });

    const markers = assignLaneBubbleOffsets(baseMarkers).sort((a, b) => a.taskNumber.localeCompare(b.taskNumber));

    return {
      key: group.anchorDate,
      dateLabel: formatTaskTimelineDate(group.anchorDate),
      leftPercent: clampTimelinePosition(groupLeftPercent),
      dotCategory: resolveTimelineGroupDotCategory(markers),
      dotStatus: resolveTimelineGroupDotStatus(markers),
      markers,
    } satisfies ProjectTaskTimelineGroup;
  });

  const totalTaskCount = asNumber(project.LinkedTaskCount) ?? allTasks.length;
  return {
    groups,
    overflowCount: Math.max(totalTaskCount - normalizedTasks.length, 0),
    todayLeftPercent: TIMELINE_CENTER_PERCENT,
  };
}

function buildProjectLocation(project: Project): string {
  const parts = [project.SiteAddress, formatSuburbLabel(project.Suburb)].map((value) => String(value || '').trim()).filter(Boolean);
  if (parts.length > 0) {
    return parts.join(', ');
  }
  return String(project.ProjectLocation || project.ProjectName || 'Location not set').trim();
}

function buildProjectDiscipline(project: Project): string {
  const structuralFee = asNumber(project.StructuralFee) ?? 0;
  const civilFee = asNumber(project.CivilFee) ?? 0;
  if (structuralFee > 0 && civilFee > 0) return 'Civil + Structural';
  if (civilFee > 0) return 'Civil';
  if (structuralFee > 0) return 'Structural';

  const scopeText = normalizeComparableText(`${project.ProjectScope || ''} ${project.ProjectType || ''}`);
  const mentionsCivil = scopeText.includes('civil');
  const mentionsStructural = scopeText.includes('structural');
  if (mentionsCivil && mentionsStructural) return 'Civil + Structural';
  if (mentionsCivil) return 'Civil';
  if (mentionsStructural) return 'Structural';
  return 'Scope pending';
}

function buildProjectTaskSummary(project: Project): string {
  const linkedTaskCount = asNumber(project.LinkedTaskCount) ?? 0;
  const openTaskCount = asNumber(project.OpenTaskCount) ?? 0;
  if (linkedTaskCount <= 0) {
    return 'No linked tasks';
  }
  const linkedLabel = `${linkedTaskCount} task${linkedTaskCount === 1 ? '' : 's'} linked`;
  return openTaskCount > 0 ? `${linkedLabel} • ${openTaskCount} open` : linkedLabel;
}

function buildProjectInvoiceSummary(project: Project): string {
  const structuralUnpaid = asNumber(project.CurrentUnpaidInvoiceAmount) ?? 0;
  const civilUnpaid = asNumber(project.CivilUnpaidInvoiceAmount) ?? 0;
  const structuralInvoiceCount = asNumber(project.CurrentInvoiceCount) ?? 0;
  const civilInvoiceCount = asNumber(project.CivilInvoiceCount) ?? 0;
  const discipline = normalizeComparableText(buildProjectDiscipline(project));
  const showStructural = structuralInvoiceCount > 0 || structuralUnpaid > 0 || discipline.includes('structural');
  const showCivil = civilInvoiceCount > 0 || civilUnpaid > 0 || discipline.includes('civil');
  const segments: string[] = [];

  if (showStructural) {
    segments.push(`S${formatCurrencyValue(structuralUnpaid, 2)} unpaid`);
  }
  if (showCivil) {
    segments.push(`C${formatCurrencyValue(civilUnpaid, 2)} unpaid`);
  }

  if (segments.length > 0) {
    return segments.join(' • ');
  }

  return 'No invoices yet';
}

function formatProjectDate(value?: string): string {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getProjectStatusBadgeTone(status?: string | null): 'active' | 'warning' | 'success' | 'muted' | 'default' {
  const normalized = normalizeComparableText(status);
  if (['active', 'in progress', 'review', 'pending', 'open'].includes(normalized)) return 'warning';
  if (['completed', 'complete', 'done', 'finished', 'paid', 'accepted'].includes(normalized)) return 'success';
  if (['archived', 'inactive', 'on hold', 'paused'].includes(normalized)) return 'muted';
  if (['cancelled', 'canceled', 'deleted', 'overdue'].includes(normalized)) return 'default';
  return 'default';
}

function normalizeProjectStatus(status?: string | null): (typeof PROJECT_STATUS_OPTIONS)[number] {
  const normalized = String(status || '').trim();
  return PROJECT_STATUS_OPTIONS.includes(normalized as (typeof PROJECT_STATUS_OPTIONS)[number])
    ? normalized as (typeof PROJECT_STATUS_OPTIONS)[number]
    : 'Active';
}

function matchesProjectLifecycleFilter(project: Project, filter: ProjectLifecycleFilter): boolean {
  if (filter === 'all') return true;

  const status = normalizeComparableText(project.Status);
  if (filter === 'active') {
    return !['completed', 'complete', 'done', 'finished', 'inactive', 'on hold', 'paused', 'hold', 'cancelled', 'canceled', 'archived', 'deleted'].includes(status);
  }
  if (filter === 'on-hold') return ['on hold', 'paused', 'hold'].includes(status);
  if (filter === 'inactive') return status === 'inactive';
  if (filter === 'completed') return ['completed', 'complete', 'done', 'finished'].includes(status);
  if (filter === 'cancelled') return ['cancelled', 'canceled'].includes(status);
  if (filter === 'archived') return true;
  return true;
}

function hasCivilFee(project: Project): boolean {
  return (asNumber(project.CivilFee) ?? 0) > 0;
}

function hasStructuralFee(project: Project): boolean {
  return (asNumber(project.StructuralFee) ?? 0) > 0;
}

function matchesDisciplineFilter(project: Project, filter: ProjectDisciplineFilter): boolean {
  if (filter === 'all') return true;

  const discipline = normalizeComparableText(buildProjectDiscipline(project));
  if (filter === 'civil') {
    return discipline.includes('civil');
  }
  if (filter === 'structural') {
    return discipline.includes('structural');
  }
  return true;
}

function getDefaultDisciplineFilter(userEmail?: string | null): ProjectDisciplineFilter {
  const normalized = String(userEmail || '').trim().toLowerCase();
  if (normalized === WILSON_EMAIL) return 'civil';
  if (normalized === YUAN_EMAIL) return 'structural';
  return 'all';
}

function getProjectListSubtitle(lifecycleFilter: ProjectLifecycleFilter): string {
  if (lifecycleFilter === 'completed') {
    return 'Search and review completed project records.';
  }
  if (lifecycleFilter === 'on-hold') {
    return 'Search and review projects currently on hold.';
  }
  if (lifecycleFilter === 'inactive') {
    return 'Search and review inactive project records.';
  }
  if (lifecycleFilter === 'cancelled') {
    return 'Search and review cancelled project records.';
  }
  if (lifecycleFilter === 'archived') {
    return 'Search and review archived or soft-deleted project records.';
  }
  if (lifecycleFilter === 'all') {
    return 'Search and review project records across all statuses.';
  }
  return 'Search and review active project records.';
}

function getProjectFilterNotice(lifecycleFilter: ProjectLifecycleFilter): string {
  const optionLabel = PROJECT_STATUS_FILTER_OPTIONS.find((option) => option.value === lifecycleFilter)?.label || 'selected status';
  if (lifecycleFilter === 'all') {
    return 'Showing project records across all statuses, including active, on hold, completed, cancelled, and archived rows.';
  }
  if (lifecycleFilter === 'archived') {
    return 'Showing archived or soft-deleted project rows so they can be reviewed and restored.';
  }
  return `Showing ${optionLabel.toLowerCase()} project records.`;
}

function getProjectStatusFilterName(lifecycleFilter: ProjectLifecycleFilter): string {
  if (lifecycleFilter === 'on-hold') return 'on hold';
  if (lifecycleFilter === 'archived') return 'archived/deleted';
  if (lifecycleFilter === 'all') return 'matching';
  return lifecycleFilter;
}

function getProjectEmptyStateDescription(
  searchQuery: string,
  disciplineFilter: ProjectDisciplineFilter,
  lifecycleFilter: ProjectLifecycleFilter
): string {
  if (searchQuery) return 'Try adjusting your search.';
  if (disciplineFilter !== 'all') {
    return `No ${disciplineFilter === 'civil' ? 'civil' : 'structural'} projects matched your current filters.`;
  }
  if (lifecycleFilter === 'active') {
    return 'No active projects are available right now.';
  }
  return `No ${getProjectStatusFilterName(lifecycleFilter)} projects are available right now.`;
}

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<ProjectSortBy>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('updated'));
  const [showMobileTimeline, setShowMobileTimeline] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState<ProjectDisciplineFilter>('all');
  const [lifecycleFilter, setLifecycleFilter] = useState<ProjectLifecycleFilter>('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, pageSize: LIST_PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [statusSavingProjectId, setStatusSavingProjectId] = useState<number | string | null>(null);
  const [documentStatusProject, setDocumentStatusProject] = useState<Project | null>(null);
  const [councilEmailProject, setCouncilEmailProject] = useState<Project | null>(null);
  const [councilEmailDraft, setCouncilEmailDraft] = useState<CouncilEmailDraft>(() => ({
    councilEngineerEmail: '',
    subject: '',
    message: buildCouncilDrainageEmailMessage(),
  }));
  const [councilEmailSending, setCouncilEmailSending] = useState(false);
  const [councilEmailError, setCouncilEmailError] = useState<string | null>(null);
  const [councilEmailNotice, setCouncilEmailNotice] = useState<string | null>(null);
  const { playSuccess, playError } = useNotificationSound();
  const { login, user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const isNarrowScreen = useIsNarrowScreen(640);
  const currentUserEmail = String(user?.userDetails || '').trim().toLowerCase();
  const aiFocus = readCompanyAiFocusTarget(location.state, 'projects');
  const aiPeriodFilter = readCompanyAiPeriodFilter(location.state, 'projects');
  const aiFollowUp = readCompanyAiFollowUpSeed(location.state);
  const hasActiveDateRange = hasDateRange(dateFrom, dateTo);
  const activeDateBasis = aiPeriodFilter?.dateBasis || 'updated date';
  const isUsingAiPeriodRange = Boolean(
    aiPeriodFilter
    && normalizeDateOnlyInput(aiPeriodFilter.startDate) === dateFrom
    && normalizeDateOnlyInput(aiPeriodFilter.endDate) === dateTo
  );

  useEffect(() => {
    setDisciplineFilter(getDefaultDisciplineFilter(currentUserEmail));
  }, [currentUserEmail]);

  useEffect(() => {
    if (!aiFocus && !aiPeriodFilter) {
      return;
    }

    setDisciplineFilter('all');
    if (aiPeriodFilter) {
      setLifecycleFilter('active');
      setSortBy('updated');
      setSortDirection('desc');
      setSearchQuery('');
      setDateFrom(normalizeDateOnlyInput(aiPeriodFilter.startDate));
      setDateTo(normalizeDateOnlyInput(aiPeriodFilter.endDate));
      return;
    }

    setLifecycleFilter('all');
    setSearchQuery((current) => current.trim() || aiFocus?.searchText || '');
  }, [aiFocus?.recordId, aiFocus?.searchText, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const timer = window.setTimeout(() => {
      loadProjects(searchQuery.trim());
    }, searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated, user, searchQuery, lifecycleFilter, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis, dateFrom, dateTo, sortBy, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, lifecycleFilter, dateFrom, dateTo, activeDateBasis, disciplineFilter]);

  const loadProjects = async (query: string = searchQuery.trim()) => {
    setLoading(true);
    setError(null);

    try {
      console.log('=== STARTING PROJECT LOAD (SWA) ===');
      console.log('User authenticated:', !!user);
      console.log('API Base URL:', '/api/projects');

      // Use Azure SQL Service with SWA cookie authentication
      const { azureSqlService } = await import('@/services/azureSqlService.js');

      const projectResult = await azureSqlService.getProjects({
        page,
        pageSize,
        sort: sortBy === 'suggested' ? 'suggested' : undefined,
        query: {
          ...(query ? { q: query } : {}),
          lifecycle: lifecycleFilter,
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(hasActiveDateRange ? { dateBasis: activeDateBasis } : {}),
        },
        useCache: !query && !hasActiveDateRange && page === 1,
        returnEnvelope: true,
      });
      const projectData = projectResult?.data || [];

      console.log('=== API RESPONSE ===');
      console.log('Projects loaded:', projectData?.length || 0);
      console.log('First project sample:', projectData?.[0]);

      setProjects(projectData || []);
      setPagination({
        page: Number(projectResult?.pagination?.page || page),
        pageSize: Number(projectResult?.pagination?.pageSize || pageSize),
        totalRecords: Number(projectResult?.pagination?.totalRecords || projectData.length || 0),
        totalPages: Math.max(Number(projectResult?.pagination?.totalPages || 1), 1),
      });
      playSuccess();

    } catch (err) {
      console.error('❌ Error loading projects:', err);

      let errorMessage = 'Unknown error';
      if (err instanceof Error) {
        errorMessage = err.message;
        if ((err as any).details?.message) {
          errorMessage = (err as any).details.message;
        }
      } else {
        errorMessage = String(err);
      }

      setError(errorMessage);
      playError();
    } finally {
      setLoading(false);
    }
  };

  // Search filter based on Azure SQL ProjectList fields
  const filteredProjects = projects.filter(project => {
    if (!matchesProjectLifecycleFilter(project, lifecycleFilter)) return false;
    if (!matchesDisciplineFilter(project, disciplineFilter)) return false;
    return true;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'suggested':
        comparison = getSuggestedProjectScore(a) - getSuggestedProjectScore(b);
        break;
      case 'code':
        comparison = String(a.ProjectCode || '').localeCompare(String(b.ProjectCode || ''));
        break;
      case 'status':
        comparison = String(a.Status || '').localeCompare(String(b.Status || ''));
        break;
      case 'updated': {
        const da = new Date(a.UpdatedAt || a.CreatedAt || 0).getTime();
        const db = new Date(b.UpdatedAt || b.CreatedAt || 0).getTime();
        comparison = da - db;
        break;
      }
      case 'address':
      default:
        comparison = buildProjectLocation(a).localeCompare(buildProjectLocation(b));
        break;
    }

    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });
  const visibleProjects = sortedProjects;
  const showDisciplineNoticeCard = !loading && !error && visibleProjects.length > 0 && disciplineFilter !== 'all';

  useEffect(() => {
    if (!aiFocus || loading) {
      return;
    }

    const focusId = getCompanyAiFocusDomId('projects', aiFocus.recordId);
    if (!focusId || !visibleProjects.some((project) => matchesCompanyAiFocusRecord(aiFocus, project.ProjectId))) {
      return;
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(focusId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (element as HTMLElement).focus === 'function') {
        (element as HTMLElement).focus({ preventScroll: true });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [aiFocus, loading, visibleProjects]);

  const handleSortChange = (value: string) => {
    const nextSortBy = value as ProjectSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
    setPage(1);
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  const useSuggestedOrder = () => {
    setSortBy('suggested');
    setSortDirection('desc');
    setPage(1);
  };

  const handleProjectStatusChange = async (project: Project, nextStatus: string) => {
    const normalizedStatus = normalizeProjectStatus(nextStatus);
    if (!project.ProjectId || normalizedStatus === normalizeProjectStatus(project.Status)) {
      return;
    }

    setStatusSavingProjectId(project.ProjectId);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateProject(project.ProjectId, { status: normalizedStatus });
      setProjects((current) => current.map((item) => (
        item.ProjectId === project.ProjectId
          ? { ...item, Status: normalizedStatus }
          : item
      )));
      playSuccess();
    } catch (err) {
      console.error('❌ Error updating project status:', err);
      playError();
    } finally {
      setStatusSavingProjectId((current) => (current === project.ProjectId ? null : current));
    }
  };

  const openCouncilEmailModal = (project: Project) => {
    setCouncilEmailProject(project);
    setCouncilEmailDraft(createCouncilEmailDraft(project));
    setCouncilEmailError(null);
    setCouncilEmailNotice(null);
  };

  const closeCouncilEmailModal = () => {
    if (councilEmailSending) return;
    setCouncilEmailProject(null);
    setCouncilEmailError(null);
  };

  const handleCouncilEmailDraftChange = (field: keyof CouncilEmailDraft, value: string) => {
    setCouncilEmailDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSendCouncilEmail = async () => {
    if (!councilEmailProject || councilEmailSending) return;

    const recipientEmail = councilEmailDraft.councilEngineerEmail.trim().toLowerCase();
    const subject = councilEmailDraft.subject.trim();
    const message = normalizeEmailMessageText(councilEmailDraft.message);

    if (!isValidEmail(recipientEmail)) {
      setCouncilEmailError('Enter a valid council engineer email address.');
      return;
    }
    if (!subject) {
      setCouncilEmailError('Subject is required.');
      return;
    }
    if (!message) {
      setCouncilEmailError('Message is required.');
      return;
    }

    setCouncilEmailSending(true);
    setCouncilEmailError(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.sendProjectCouncilEmail(councilEmailProject.ProjectId, {
        councilEngineerEmail: recipientEmail,
        subject,
        message,
      });
      const sentAt = result?.sentAt || new Date().toISOString();
      const sentBy = result?.sentBy || COUNCIL_DRAINAGE_EMAIL_SENDER;
      setProjects((current) => current.map((project) => (
        project.ProjectId === councilEmailProject.ProjectId
          ? {
              ...project,
              CouncilEngineerEmail: recipientEmail,
              CouncilDrainageEmailLastSentAt: sentAt,
              CouncilDrainageEmailLastSentBy: sentBy,
            }
          : project
      )));
      setCouncilEmailNotice(`Council trace email sent to ${recipientEmail}.`);
      setCouncilEmailProject(null);
      playSuccess();
    } catch (err) {
      const messageText = err instanceof Error ? err.message : 'Unknown error';
      setCouncilEmailError(messageText);
      playError();
    } finally {
      setCouncilEmailSending(false);
    }
  };

  return (
    <div className="dgf-page">
      <PageHeader
        title="Projects"
        subtitle={getProjectListSubtitle(lifecycleFilter)}
        itemCount={visibleProjects.length}
        gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="🏗️"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        searchEnabled={true}
        searchQuery={searchQuery}
        searchPlaceholder="Search projects"
        onSearchChange={setSearchQuery}
        sortEnabled={true}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'suggested', label: 'Suggested' },
          { value: 'updated', label: 'Updated' },
          { value: 'address', label: 'Address' },
          { value: 'code', label: 'Project Number' },
          { value: 'status', label: 'Status' },
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        mobileHeaderAction={isNarrowScreen ? (
          <Button
            className="project-list__mobile-order-button"
            type="button"
            variant="secondary"
            size="sm"
            shape="pill"
            iconBefore={<span aria-hidden="true">✨</span>}
            aria-label={sortBy === 'suggested' ? 'Using suggested order' : 'Use suggested order'}
            title={sortBy === 'suggested' ? 'Using suggested order' : 'Use suggested order'}
            onClick={useSuggestedOrder}
            disabled={sortBy === 'suggested' && sortDirection === 'desc'}
          >
            Order
          </Button>
        ) : undefined}
        actions={isNarrowScreen
          ? [
              {
                label: showMobileTimeline ? 'Hide timeline' : 'Show timeline',
                icon: showMobileTimeline ? '−' : '+',
                onClick: () => setShowMobileTimeline((current) => !current),
                variant: 'secondary',
              },
            ]
          : [
              {
                label: sortBy === 'suggested' ? 'Using suggested order' : 'Use suggested order',
                icon: '✨',
                onClick: useSuggestedOrder,
                variant: 'secondary',
                disabled: sortBy === 'suggested' && sortDirection === 'desc',
              },
            ]}
        filters={(
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: isNarrowScreen ? '100%' : 'auto' }}>
            <div style={{ minWidth: isNarrowScreen ? '100%' : '200px' }}>
              <Select
                value={lifecycleFilter}
                onChange={(e) => setLifecycleFilter(e.target.value as ProjectLifecycleFilter)}
                fullWidth
                density="compact"
                options={PROJECT_STATUS_FILTER_OPTIONS}
              />
            </div>
            <div style={{ minWidth: isNarrowScreen ? '100%' : '200px' }}>
              <Select
                value={disciplineFilter}
                onChange={(e) => setDisciplineFilter(e.target.value as ProjectDisciplineFilter)}
                fullWidth
                density="compact"
                options={[
                  { value: 'all', label: 'All disciplines' },
                  { value: 'civil', label: 'Civil only' },
                  { value: 'structural', label: 'Structure only' },
                ]}
              />
            </div>
            <div style={{ minWidth: isNarrowScreen ? 'calc(50% - 5px)' : '150px' }}>
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateFrom}
                onChange={(e) => setDateFrom(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            <div style={{ minWidth: isNarrowScreen ? 'calc(50% - 5px)' : '150px' }}>
              <Input
                type="date"
                density="compact"
                fullWidth
                value={dateTo}
                onChange={(e) => setDateTo(normalizeDateOnlyInput(e.target.value))}
              />
            </div>
            {hasActiveDateRange ? (
              <Button
                variant="secondary"
                shape="pill"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Clear Dates
              </Button>
            ) : null}
          </div>
        )}
      />

      <PageSection width="wide" padding="default">
        {(aiFocus || hasActiveDateRange) ? (
          <Card padding="md">
            <div className="dgf-notice__row">
              <div className="dgf-notice dgf-notice--info" style={{ flex: 1 }}>
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI projects for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'updated date'}</strong>.
                  </>
                ) : hasActiveDateRange ? (
                  <>
                    Filtering projects from <strong>{dateFrom || 'any start'}</strong> to <strong>{dateTo || 'any end'}</strong> by <strong>{aiPeriodFilter?.dateBasis || 'updated date'}</strong>.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and project filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
              {aiFollowUp?.prompt ? (
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => navigate(`/ai?prompt=${encodeURIComponent(aiFollowUp.prompt)}`, {
                    state: {
                      companyAiFollowUp: aiFollowUp,
                    },
                  })}
                >
                  Continue in workspace
                </Button>
              ) : null}
            </div>
          </Card>
        ) : null}

        {lifecycleFilter !== 'active' ? (
          <Card padding="md">
            <div className="dgf-notice__row">
              <div className="dgf-meta-muted project-list-notice__message">
                {getProjectFilterNotice(lifecycleFilter)}
              </div>
              <Button variant="secondary" shape="pill" onClick={() => setLifecycleFilter('active')}>
                Show Active Only
              </Button>
            </div>
          </Card>
        ) : null}

        {councilEmailNotice ? (
          <Card padding="sm" className="project-list-hint-bar">
            <div className="dgf-notice__row">
              <div className="dgf-meta-muted project-list-notice__message">
                {councilEmailNotice}
              </div>
              <Button variant="secondary" shape="pill" size="sm" onClick={() => setCouncilEmailNotice(null)}>
                Dismiss
              </Button>
            </div>
          </Card>
        ) : null}

        {showDisciplineNoticeCard ? (
          <Card padding="sm" className="project-list-hint-bar">
            <div className="dgf-notice__row">
              <div className="dgf-meta-muted project-list-notice__message">
                Showing {disciplineFilter === 'civil' ? 'Civil only' : 'Structural only'} projects. Switch to All disciplines to see everything.
              </div>
              <Button variant="secondary" shape="pill" size="sm" onClick={() => setDisciplineFilter('all')}>
                Show All Disciplines
              </Button>
            </div>
          </Card>
        ) : null}

        {error && (
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⚠️</div>
              <div className="dgf-state-card__title">Error Loading Projects</div>
              <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
              <div className="dgf-state-card__actions">
                <Button onClick={() => loadProjects()} variant="secondary" shape="pill">
                  Retry
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await login();
                      setTimeout(() => loadProjects(), 1000);
                    } catch (err) {
                      console.error('Re-authentication failed:', err);
                    }
                  }}
                  variant="primary"
                  shape="pill"
                >
                  Re-authenticate
                </Button>
              </div>
            </div>
          </Card>
        )}

        {loading && (
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⏳</div>
              <div>Loading projects...</div>
            </div>
          </Card>
        )}

        {!loading && visibleProjects.length > 0 && (
          <div style={{ display: 'grid', gap: '12px' }}>
            {visibleProjects.map((project) => {
              const locationLabel = buildProjectLocation(project);
              const locationComparable = normalizeComparableText(locationLabel);
              const councilLabel = replaceInternalOwnerEmails(String(project.Council || '').trim());
              const projectStatus = normalizeProjectStatus(project.Status);
              const projectStatusTone = getProjectStatusBadgeTone(projectStatus);
              const isSavingProjectStatus = statusSavingProjectId === project.ProjectId;
              const projectNameComparable = normalizeComparableText(project.ProjectName);
              const disciplineLabel = buildProjectDiscipline(project);
              const taskSummary = buildProjectTaskSummary(project);
              const invoiceSummary = buildProjectInvoiceSummary(project);
              const taskTimeline = buildProjectTaskTimeline(project);
              const taskTimelineVisibleCount = taskTimeline.groups.reduce((sum, group) => sum + group.markers.length, 0);
              const taskTimelineStageWidth = taskTimelineVisibleCount > 2
                ? Math.max(taskTimeline.groups.length * 108, taskTimelineVisibleCount * 68)
                : null;
              const totalPriceLabel = formatCurrency(project.QuotationAmount);
              const showCouncilEmailAction = hasPositiveCivilFee(project);
              const quickDescription = replaceInternalOwnerEmails(project.Description);
              const quickDescriptionComparable = normalizeComparableText(quickDescription);
              const updatedLabel = formatProjectDate(project.UpdatedAt || project.CreatedAt);
              const isAiFocusTarget = matchesCompanyAiFocusRecord(aiFocus, project.ProjectId);
              const normalizedHeaderText = normalizeComparableText(`${locationLabel} ${councilLabel}`);
              const showQuickDescription = Boolean(
                quickDescription &&
                quickDescriptionComparable !== locationComparable &&
                quickDescriptionComparable !== normalizedHeaderText &&
                !quickDescriptionComparable.includes(locationComparable) &&
                !(projectNameComparable && quickDescriptionComparable.includes(projectNameComparable))
              );

              return (
                <DataRow
                  key={project.ProjectId}
                  id={getCompanyAiFocusDomId('projects', project.ProjectId) || undefined}
                  className={isAiFocusTarget ? 'project-list-row dgf-row-focus-target' : 'project-list-row'}
                  density="compact"
                  onClick={() => navigate(`/projects/${project.ProjectId}`)}
                  title={(
                    <div className="project-list-row__header-main">
                      <span className="project-list-row__header-code">
                        {project.ProjectCode || 'N/A'}
                      </span>
                      <span className="project-list-row__header-address">
                        {locationLabel}
                      </span>
                    </div>
                  )}
                  subtitle={councilLabel || undefined}
                  description={showQuickDescription ? quickDescription : undefined}
                  amount={<span className="project-list-row__badge project-list-row__badge--price">{totalPriceLabel}</span>}
                  status={(
                    <div className="project-list-row__badges">
                      <div
                        className={[
                          'project-list-row__status-editor',
                          `project-list-row__status-editor--${projectStatusTone}`,
                          isSavingProjectStatus ? 'project-list-row__status-editor--saving' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <select
                          className="project-list-row__status-select"
                          value={projectStatus}
                          disabled={isSavingProjectStatus}
                          aria-label={`Project status for ${project.ProjectCode || locationLabel}`}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            event.stopPropagation();
                            void handleProjectStatusChange(project, event.target.value);
                          }}
                        >
                          {PROJECT_STATUS_OPTIONS.map((statusOption) => (
                            <option key={statusOption} value={statusOption}>
                              {statusOption}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        className="project-list-row__quick-actions"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="project-list-row__text-action project-list-row__document-status-button"
                          title={`Show document status for ${project.ProjectCode || locationLabel}`}
                          aria-label={`Project Status for ${project.ProjectCode || locationLabel}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setDocumentStatusProject(project);
                          }}
                        >
                          <FileSearch size={13} strokeWidth={2.4} aria-hidden="true" />
                          <span>Project Status</span>
                        </button>
                        {showCouncilEmailAction ? (
                          <button
                            type="button"
                            className="project-list-row__text-action project-list-row__council-email-button"
                            title={`Send drainage approval update for ${project.ProjectCode || locationLabel}`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              openCouncilEmailModal(project);
                            }}
                          >
                            <Mail size={13} strokeWidth={2.4} aria-hidden="true" />
                            <span>Council</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="project-list-row__text-action project-list-row__client-update-button"
                          title={`Notify client of progress for ${project.ProjectCode || locationLabel}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            navigate(`/projects/${project.ProjectId}/client-updates`);
                          }}
                        >
                          <Bell size={13} strokeWidth={2.4} aria-hidden="true" />
                          <span>Client</span>
                        </button>
                        {showCouncilEmailAction && !isNarrowScreen ? (
                          <span className="project-list-row__trace-age">
                            {formatLastTraceAge(project.CouncilDrainageEmailLastSentAt)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )}
                  footer={(
                    <div className="project-list-row__meta">
                      {taskTimeline.groups.length > 0 && (!isNarrowScreen || showMobileTimeline) ? (
                        <div className="project-list-row__timeline" aria-label={`Project task timeline showing ${taskTimelineVisibleCount} tasks`}>
                          <div className="project-list-row__timeline-scroll">
                            <div
                              className="project-list-row__timeline-stage"
                              style={taskTimelineStageWidth ? { minWidth: `${taskTimelineStageWidth}px` } : undefined}
                            >
                              <div className="project-list-row__timeline-line" />
                              <div
                                className="project-list-row__timeline-today"
                                style={{ left: `${taskTimeline.todayLeftPercent}%` }}
                              >
                                <div className="project-list-row__timeline-today-line" />
                                <span className="project-list-row__timeline-today-pill">today</span>
                              </div>
                              {taskTimeline.groups.map((group) => (
                                <div
                                  key={group.key}
                                  className="project-list-row__timeline-event"
                                  style={{ left: `${group.leftPercent}%` }}
                                >
                                  <span className={[
                                    'project-list-row__timeline-stem',
                                    `project-list-row__timeline-stem--category-${group.dotCategory.toLowerCase().replace(/\s+/g, '-')}`,
                                    `project-list-row__timeline-stem--status-${group.dotStatus.toLowerCase().replace(/\s+/g, '-')}`,
                                  ].join(' ')} />
                                  {group.markers.map((marker) => (
                                    <React.Fragment key={marker.key}>
                                      <button
                                        type="button"
                                        className="project-list-row__timeline-bubble-shell"
                                        style={{ ['--timeline-bubble-offset' as string]: `${marker.bubbleOffset}px` }}
                                        title={`${marker.taskNumber} • ${marker.title} • ${marker.taskType} • ${marker.status} • ${marker.detailLabel}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(marker.taskId != null ? `/tasks?id=${marker.taskId}` : '/tasks');
                                        }}
                                      >
                                        <span
                                          className={[
                                            'project-list-row__badge',
                                            'project-list-row__timeline-callout',
                                            `project-list-row__timeline-callout--category-${marker.category.toLowerCase().replace(/\s+/g, '-')}`,
                                            `project-list-row__timeline-callout--status-${marker.status.toLowerCase().replace(/\s+/g, '-')}`,
                                          ].join(' ')}
                                        >
                                          <span className="project-list-row__timeline-callout-code">{marker.taskNumber}</span>
                                        </span>
                                        <span
                                          className={[
                                            'project-list-row__timeline-tooltip',
                                            `project-list-row__timeline-tooltip--${marker.tooltipSide}`,
                                            `project-list-row__timeline-tooltip--status-${marker.status.toLowerCase().replace(/\s+/g, '-')}`,
                                          ].join(' ')}
                                        >
                                          <span className="project-list-row__timeline-tooltip-title">{marker.taskNumber} · {marker.title}</span>
                                          <span className="project-list-row__timeline-tooltip-copy">{marker.status} · {marker.detailLabel}</span>
                                        </span>
                                      </button>
                                    </React.Fragment>
                                  ))}
                                  <span
                                    className={[
                                      'project-list-row__timeline-dot',
                                      `project-list-row__timeline-dot--category-${group.dotCategory.toLowerCase().replace(/\s+/g, '-')}`,
                                      `project-list-row__timeline-dot--status-${group.dotStatus.toLowerCase().replace(/\s+/g, '-')}`,
                                    ].join(' ')}
                                  />
                                  <span className="project-list-row__timeline-date">
                                    {group.dateLabel}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div className="dgf-meta-row project-list-row__stats">
                        <span><strong>Service:</strong> {disciplineLabel}</span>
                        <span><strong>Tasks:</strong> {taskSummary}</span>
                        <span><strong>Updated:</strong> {updatedLabel}</span>
                        <span><strong>Invoices:</strong> {invoiceSummary}</span>
                        {showCouncilEmailAction && isNarrowScreen ? (
                          <span><strong>Council:</strong> {formatLastTraceAge(project.CouncilDrainageEmailLastSentAt)}</span>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              );
            })}
            <ListPaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              currentCount={visibleProjects.length}
              pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
            />
          </div>
        )}

        {!loading && visibleProjects.length === 0 && !error && (
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '64px' }}>📁</span>}
              title="No projects found"
              description={getProjectEmptyStateDescription(searchQuery, disciplineFilter, lifecycleFilter)}
              action={
                disciplineFilter !== 'all' ? (
                  <Button variant="secondary" shape="pill" size="sm" onClick={() => setDisciplineFilter('all')}>
                    Show All Disciplines
                  </Button>
                ) : null
              }
            />
          </Card>
        )}
      </PageSection>

      {documentStatusProject ? (
        <ProjectDocumentStatusPanel
          open
          presentation="modal"
          onClose={() => setDocumentStatusProject(null)}
          projectId={documentStatusProject.ProjectId}
          projectNumber={String(documentStatusProject.ProjectCode || documentStatusProject.ProjectId)}
        />
      ) : null}

      <Modal
        open={Boolean(councilEmailProject)}
        onClose={closeCouncilEmailModal}
        title={councilEmailProject ? `Council Trace · ${councilEmailProject.ProjectCode || councilEmailProject.ProjectId}` : 'Council Trace'}
        size="lg"
        footer={(
          <>
            <Button variant="ghost" onClick={closeCouncilEmailModal} disabled={councilEmailSending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => { void handleSendCouncilEmail(); }} disabled={councilEmailSending}>
              {councilEmailSending ? 'Sending...' : 'Send council email'}
            </Button>
          </>
        )}
      >
        <div className="project-council-email-modal">
          {councilEmailProject ? (
            <div className="invoice-reminder-modal__summary">
              <div><strong>Project:</strong> {councilEmailProject.ProjectCode || councilEmailProject.ProjectId}</div>
              <div><strong>Address:</strong> {buildProjectLocation(councilEmailProject)}</div>
              <div><strong>Council:</strong> {councilEmailProject.Council || 'Not set'}</div>
            </div>
          ) : null}

          {councilEmailError ? (
            <div className="dgf-notice dgf-notice--danger">{councilEmailError}</div>
          ) : null}

          <div className="project-council-email-modal__fields">
            <Input
              label="Council engineer email"
              type="email"
              value={councilEmailDraft.councilEngineerEmail}
              onChange={(event) => handleCouncilEmailDraftChange('councilEngineerEmail', event.target.value)}
              fullWidth
              required
            />
            <div className="invoice-reminder-modal__field">
              <span className="invoice-reminder-modal__label">From</span>
              <div className="invoice-reminder-modal__value">{COUNCIL_DRAINAGE_EMAIL_SENDER}</div>
            </div>
            <div className="invoice-reminder-modal__field">
              <span className="invoice-reminder-modal__label">BCC</span>
              <div className="invoice-reminder-modal__value">{COUNCIL_DRAINAGE_EMAIL_BCC}</div>
            </div>
            <Input
              className="project-council-email-modal__field--full"
              label="Subject"
              value={councilEmailDraft.subject}
              onChange={(event) => handleCouncilEmailDraftChange('subject', event.target.value)}
              fullWidth
              required
            />
            <Textarea
              className="project-council-email-modal__field--full"
              label="Message"
              value={councilEmailDraft.message}
              onChange={(event) => handleCouncilEmailDraftChange('message', event.target.value)}
              fullWidth
              rows={10}
              required
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectList;
